'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { fetchAllPaddles, buildCategories_list, type Paddle, type CategoryInfo } from '@/lib/services/paddleService';
import { useAuth } from '@/contexts/AuthContext';

interface NavigationProps {
  transparent?: boolean;
}

export default function Navigation({ transparent = false }: NavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Paddle[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [allPaddles, setAllPaddles] = useState<Paddle[]>([]);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const catRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearch(false);
        setSearchResults([]);
      }
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setShowCategories(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch paddles from Supabase on mount
  useEffect(() => {
    fetchAllPaddles().then(data => {
      setAllPaddles(data);
      setCategories(buildCategories_list(data));
    });
  }, []);

  const handleSearch = (q: string) => {
    setSearchQuery(q);
    if (q.length > 0) {
      const lowerQ = q.toLowerCase();
      const words = lowerQ.split(/\s+/).filter(Boolean);
      const results = allPaddles
        .filter(p => {
          const combined = `${p.brand} ${p.name}`.toLowerCase();
          return words.every(word => combined.includes(word));
        })
        .slice(0, 50);
      setSearchResults(results);
      setShowSearch(true);
    } else {
      setSearchResults([]);
      setShowSearch(false);
    }
  };

  const isTransparentMode = transparent && !isScrolled;

  return (
    <>
      <header className={`navigationContainer${isScrolled ? ' scrolled' : ''}${isTransparentMode ? ' transparent' : ''}`}>
        <div className="navigation">
          {/* Left */}
          <div className="flex items-center gap-9">
            {/* Logo */}
            <Link href="/" className="flex items-center flex-shrink-0 group">
              <span
                className="font-black transition-opacity duration-200 group-hover:opacity-70"
                style={{
                  color: isTransparentMode ? '#FFFFFF' : '#0A0A0A',
                  letterSpacing: '-0.06em',
                  fontSize: '18px',
                  fontWeight: 900,
                }}
              >PADDLE</span>
              <span
                className="font-black"
                style={{
                  color: '#D90012',
                  letterSpacing: '-0.06em',
                  fontSize: '18px',
                  fontWeight: 900,
                }}
              >VS</span>
            </Link>

            {/* Search */}
            <div ref={searchRef} className="relative hidden md:block">
              <div
                className="flex items-center rounded-full h-10 px-5 gap-3 transition-all"
                style={{
                  background: isTransparentMode ? 'rgba(255,255,255,0.11)' : 'rgba(0,0,0,0.05)',
                  border: isTransparentMode ? '1.5px solid rgba(255,255,255,0.16)' : '2px solid rgba(0,0,0,0.09)',
                  width: 210,
                  transitionDuration: '0.2s',
                  transitionTimingFunction: 'cubic-bezier(0.4,0,0.2,1)',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill={isTransparentMode ? 'rgba(255,255,255,0.4)' : 'rgba(10,10,10,0.3)'} style={{ flexShrink: 0 }}>
                  <path d="M6.5 0a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13zm0 1a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11zm4.78 9.72l3.5 3.5-.7.7-3.5-3.5.7-.7z"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search paddles"
                  value={searchQuery}
                  onChange={e => handleSearch(e.target.value)}
                  className="bg-transparent border-none outline-none text-xs w-full font-semibold"
                  style={{ color: isTransparentMode ? '#FFFFFF' : '#0A0A0A', fontFamily: 'inherit', letterSpacing: '-0.01em' }}
                  onFocus={e => {
                    const parent = e.target.parentElement;
                    if (parent) {
                      parent.style.border = '2px solid rgba(217,0,18,0.5)';
                      parent.style.background = isTransparentMode ? 'rgba(255,255,255,0.14)' : '#FFFFFF';
                      parent.style.boxShadow = '0 0 0 4px rgba(217,0,18,0.1)';
                    }
                  }}
                  onBlur={e => {
                    const parent = e.target.parentElement;
                    if (parent) {
                      parent.style.border = isTransparentMode ? '1.5px solid rgba(255,255,255,0.16)' : '2px solid rgba(0,0,0,0.09)';
                      parent.style.background = isTransparentMode ? 'rgba(255,255,255,0.11)' : 'rgba(0,0,0,0.05)';
                      parent.style.boxShadow = 'none';
                    }
                  }}
                />
              </div>
              {showSearch && searchResults.length > 0 && (
                <div
                  className="absolute top-full left-0 mt-2.5 w-80 z-[200]"
                  style={{
                    background: '#FFFFFF',
                    border: '1.5px solid rgba(0,0,0,0.08)',
                    borderRadius: '18px',
                    boxShadow: '0 28px 72px rgba(0,0,0,0.18), 0 4px 14px rgba(0,0,0,0.07)',
                    maxHeight: '400px',
                    overflowY: 'auto',
                  }}
                >
                  {searchResults.map((p, i) => (
                    <Link
                      key={p.id}
                      href={`/paddles/${p.slug}`}
                      className="flex items-center gap-3.5 px-5 py-3.5 transition-all"
                      style={{
                        borderBottom: i < searchResults.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                        transitionDuration: '0.15s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(217,0,18,0.03)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      onClick={() => { setShowSearch(false); setSearchQuery(''); }}
                    >
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-black flex-shrink-0"
                        style={{ background: 'rgba(217,0,18,0.08)', color: '#D90012', border: '1.5px solid rgba(217,0,18,0.14)' }}
                      >
                        {p.brand.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate" style={{ color: '#0A0A0A', letterSpacing: '-0.015em' }}>{p.name}</div>
                        <div className="text-xs mt-0.5 font-semibold" style={{ color: 'rgba(10,10,10,0.38)' }}>{p.brand}</div>
                      </div>
                      <div className="text-sm font-black flex-shrink-0 tabular-nums" style={{ color: '#D90012' }}>{p.score}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Categories dropdown */}
            <div ref={catRef} className="relative hidden md:block">
              <button
                className="nav-link flex items-center gap-1.5"
                style={{ color: isTransparentMode ? 'rgba(255,255,255,0.52)' : undefined }}
                onMouseEnter={() => setShowCategories(true)}
                onMouseLeave={() => setShowCategories(false)}
              >
                Categories
                <svg width="7" height="7" viewBox="0 0 10 10" fill="currentColor" style={{ opacity: 0.45, marginTop: 1 }}>
                  <path d="M5 7L1 3h8L5 7z"/>
                </svg>
              </button>
              {showCategories && (
                <div
                  className="fixed left-0 w-full z-50"
                  style={{
                    top: 72,
                    background: 'rgba(255,255,255,1)',
                    backdropFilter: 'blur(40px) saturate(260%)',
                    borderBottom: '1.5px solid rgba(0,0,0,0.07)',
                    boxShadow: '0 28px 72px rgba(0,0,0,0.14)',
                  }}
                  onMouseEnter={() => setShowCategories(true)}
                  onMouseLeave={() => setShowCategories(false)}
                >
                  <div className="max-w-container mx-auto px-8 py-10">
                    <div className="grid grid-cols-3 gap-3">
                      {categories.map(cat => (
                        <Link
                          key={cat.slug}
                          href={`/categories/${cat.slug}`}
                          className="flex items-start gap-4 p-5 rounded-2xl transition-all group"
                          style={{ border: '1.5px solid transparent', transitionDuration: '0.2s' }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = 'rgba(217,0,18,0.03)';
                            e.currentTarget.style.border = '1.5px solid rgba(217,0,18,0.12)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.border = '1.5px solid transparent';
                          }}
                          onClick={() => setShowCategories(false)}
                        >
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                            style={{ background: 'rgba(217,0,18,0.07)', border: '1.5px solid rgba(217,0,18,0.12)' }}
                          >
                            {cat.icon}
                          </div>
                          <div className="min-w-0">
                            <div className="font-black text-sm mb-1 transition-colors leading-tight" style={{ color: '#0A0A0A', letterSpacing: '-0.025em' }}>{cat.name}</div>
                            <div className="text-xs leading-relaxed" style={{ color: 'rgba(10,10,10,0.42)' }}>{cat.description}</div>
                            <div className="text-xs mt-2 font-black" style={{ color: '#D90012' }}>{cat.count} paddles</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                    <div className="mt-8 pt-6" style={{ borderTop: '1.5px solid rgba(0,0,0,0.06)' }}>
                      <div className="flex justify-end">
                        <Link
                          href="/categories"
                          className="versusButton pill text-xs"
                          onClick={() => setShowCategories(false)}
                        >
                          See all categories
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quick links */}
            <div className="hidden lg:flex items-center gap-7">
              <Link
                href="/categories/carbon-fiber"
                className="nav-link"
                style={{ color: isTransparentMode ? 'rgba(255,255,255,0.5)' : undefined }}
              >Carbon Fiber</Link>
              <Link
                href="/categories/tournament"
                className="nav-link"
                style={{ color: isTransparentMode ? 'rgba(255,255,255,0.5)' : undefined }}
              >Tournament</Link>
              <Link
                href="/top-paddles"
                className="nav-link"
                style={{ color: isTransparentMode ? 'rgba(255,255,255,0.5)' : undefined }}
              >Top Paddles</Link>
            </div>
          </div>

          {/* Right */}
          <div className="flex items-center gap-4">
            {user && (
              <Link
                href="/saved-comparisons"
                className="nav-link hidden md:block"
                style={{ color: isTransparentMode ? 'rgba(255,255,255,0.5)' : undefined }}
              >
                Saved
              </Link>
            )}
            <Link
              href="/compare"
              className="versusButton"
              style={{ fontSize: '10px', padding: '10px 20px', borderRadius: 'var(--radius-md)' }}
            >
              Compare
            </Link>
          </div>
        </div>
      </header>
    </>
  );
}
